/**
 * Static Tag Assignment Hooks — bulk assign/unassign tags on leads lists.
 * Assigned tags surface through the list-performance-tags response (merged
 * server-side at read time), so every mutation invalidates that query by
 * predicate — same approach as useListTagRules — plus the static-tags key.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import api from '@/services/api';

export interface BulkListStaticTagsInput {
  staticTagIds: string[];
  wiserIds: number[];
}

const STATIC_TAGS_KEY = ['smswiser', 'static-tags'] as const;

/** Merged tags live in the list-performance-tags response (2h client cache). */
function invalidateTagSurfaces(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey.includes('list-performance-tags'),
  });
  queryClient.invalidateQueries({ queryKey: STATIC_TAGS_KEY });
}

export function useBulkAssignStaticTags(countryKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkListStaticTagsInput) => {
      const res = await api.post<{ data: { assigned: number } }>(
        `/smswiser/list-static-tags/${encodeURIComponent(countryKey)}/bulk-assign`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => invalidateTagSurfaces(queryClient),
  });
}

export function useBulkUnassignStaticTags(countryKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkListStaticTagsInput) => {
      const res = await api.post<{ data: { removed: number } }>(
        `/smswiser/list-static-tags/${encodeURIComponent(countryKey)}/bulk-unassign`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => invalidateTagSurfaces(queryClient),
  });
}
